import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";

const EditPatientInvoice = () => {
  const { invoice_id } = useParams();
  const navigate = useNavigate();

  const [invoice, setInvoice] = useState(null);
  const [patient, setPatient] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [containerStyle, setContainerStyle] = useState({});

  const [formData, setFormData] = useState({
    invoice_date: "",
    discount: 0, // added discount key
    items: []
  });

  // Responsive container style for sidebar
  useEffect(() => {
    const updateContainerStyle = () => {
      const baseStyle = {
        maxWidth: "1200px",
        margin: "0 auto",
        padding: "20px",
        fontFamily: "Arial, sans-serif",
        backgroundColor: "#f8f9fa",
        minHeight: "100vh",
        transition: "margin-left 0.3s ease",
      };

      if (window.innerWidth >= 1024) {
        setContainerStyle({ ...baseStyle, marginLeft: "250px" });
      } else {
        setContainerStyle({ ...baseStyle, marginLeft: "0" });
      }
    };

    updateContainerStyle();
    window.addEventListener("resize", updateContainerStyle);
    return () => window.removeEventListener("resize", updateContainerStyle);
  }, []);

  // Fetch invoice
  useEffect(() => {
    if (invoice_id) fetchInvoiceData();
    else {
      setError("No invoice ID provided in URL");
      setLoading(false);
    }
  }, [invoice_id]);

  const fetchInvoiceData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(
        `https://newerp.awasthivitality.com/api/invoices/${invoice_id}/edit`
      );

      if (response.data.success) {
        const data = response.data.data;
        setInvoice({
          invoice_id: data.invoice_id,
          invoice_no: data.invoice_no,
          invoice_date: data.invoice_date,
          invoice_amount: data.invoice_amount
        });
        setPatient(data.patient);
        setProducts(data.products);

        setFormData({
          invoice_date: data.invoice_date,
          discount: Number(data.discount ?? 0), // fetch discount
          items: data.invoice_details.map(detail => ({
            detail_id: detail.detail_id,
            description: detail.description,
            quantity: detail.quantity,
            amount: detail.amount,
            product_id: detail.product_id,
            product_name: detail.product_name,
            final_price: detail.final_price
          }))
        });
      } else {
        setError("Failed to fetch invoice data: " + (response.data.message || "Unknown error"));
      }
    } catch (err) {
      if (err.response?.status === 400) setError("Invalid invoice ID provided. Please check the URL.");
      else if (err.response?.status === 404) setError("Invoice not found with ID: " + invoice_id);
      else setError("Error fetching invoice data: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.items];
    if (field === "product_id") {
      const selectedProduct = products.find(p => p.product_id == value);
      if (selectedProduct) {
        updatedItems[index] = {
          ...updatedItems[index],
          product_id: selectedProduct.product_id,
          description: selectedProduct.product_name,
          amount: selectedProduct.final_price,
          quantity: updatedItems[index].quantity || 1
        };
      } else {
        updatedItems[index] = { ...updatedItems[index], product_id: null, description: "", amount: "" };
      }
    } else if (field === "quantity") {
      updatedItems[index][field] = value === "" ? "" : Number(value);
      const unitPrice = Number(updatedItems[index].amount) || 0;
      updatedItems[index].total = unitPrice * (Number(value) || 0);
    } else if (field === "amount") {
      updatedItems[index][field] = value === "" ? "" : Number(value);
      if (!updatedItems[index].product_id) {
        updatedItems[index].total = (Number(value) || 0) * (Number(updatedItems[index].quantity) || 0);
      }
    } else {
      updatedItems[index][field] = field === "description" ? value : value === "" ? "" : Number(value);
    }
    setFormData(prev => ({ ...prev, items: updatedItems }));
  };

  const addNewItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [
        ...prev.items,
        { description: "", quantity: 1, amount: "", product_id: null, product_name: null, final_price: null }
      ]
    }));
  };

  const removeItem = (index) => {
    setFormData(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  };

  const totalAmount = formData.items.reduce(
    (sum, item) => sum + ((Number(item.amount) || 0) * (Number(item.quantity) || 0)),
    0
  );

  const totalAfterDiscount = Math.max(totalAmount - (Number(formData.discount) || 0), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationErrors({});

    const validItems = formData.items.filter(item => item.description.trim() !== "" && item.amount > 0 && item.quantity > 0);
    if (validItems.length === 0) {
      setValidationErrors({ products: ["Please add at least one valid item!"] });
      return;
    }

    const updateData = {
      patient_id: patient?.patient_id,
      invoice_date: formData.invoice_date,
      discount: Number(formData.discount) || 0,
      payment_status: "Paid",
      products: validItems.map(item => ({
        description: item.description.trim(),
        quantity: item.quantity || 1,
        amount: item.amount,
        product_id: item.product_id,
        product_name: item.product_name,
        final_price: item.final_price
      })),
    };

    try {
      setSubmitting(true);
      const response = await axios.put(
        `https://newerp.awasthivitality.com/api/invoices/${invoice_id}/update`,
        updateData,
        { headers: { "Content-Type": "application/json", Accept: "application/json" } }
      );

      if (response.data.success) {
        const apiTotal = Number(response.data.data?.total_amount ?? response.data.data?.invoice_amount ?? 0);
        alert(`Invoice updated successfully!\nInvoice No: ${response.data.data.invoice_no}\nSubtotal: ₹${(apiTotal + (Number(formData.discount) || 0)).toFixed(2)}\nDiscount: ₹${(Number(formData.discount) || 0).toFixed(2)}\nTotal Amount: ₹${apiTotal.toFixed(2)}`);
        navigate(-1);
      } else alert(`Error: ${response.data.message}`);
    } catch (error) {
      if (error.response?.status === 422 && error.response?.data?.errors) setValidationErrors(error.response.data.errors);
      else alert(`Failed to update invoice: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const styles = {
    header: { textAlign: "center", color: "#333", marginBottom: "30px", fontSize: "28px", fontWeight: "bold" },
    patientInfo: { background: "white", padding: "15px", borderRadius: "10px", boxShadow: "0 2px 6px rgba(0,0,0,0.1)", marginBottom: "20px" },
    label: { fontWeight: "bold", color: "#555", marginRight: "10px" },
    table: { width: "100%", borderCollapse: "collapse", marginBottom: "30px", background: "white", borderRadius: "12px", overflow: "hidden", boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)" },
    tableHeader: { backgroundColor: "rgb(47 178 231)", padding: "15px", textAlign: "left", fontWeight: "bold", color: "white" },
    tableCell: { padding: "12px 15px", borderBottom: "1px solid #dee2e6" },
    selectInput: { width: "100%", padding: "8px", border: "1px solid #ddd", borderRadius: "6px", fontSize: "15px" },
    numberInput: { width: "100%", padding: "8px", border: "1px solid #ddd", borderRadius: "6px", textAlign: "right" },
    totalAmount: { fontWeight: "bold", backgroundColor: "#f8f9fa", fontSize: "16px" },
    discountInput: { width: "100px", padding: "8px", border: "1px solid #ddd", borderRadius: "6px", textAlign: "right" },
    submitButton: { width: "100%", padding: "15px", backgroundColor: "#28a745", color: "white", border: "none", borderRadius: "8px", fontSize: "18px", fontWeight: "bold", cursor: "pointer" },
    submitButtonDisabled: { backgroundColor: "#6c757d", cursor: "not-allowed" },
    addButton: { backgroundColor: "rgb(47 178 231)", color: "white", border: "none", borderRadius: "6px", padding: "10px 20px", cursor: "pointer", fontSize: "16px", fontWeight: "bold", marginBottom: "20px" },
    removeButton: { backgroundColor: "#dc3545", color: "white", border: "none", borderRadius: "50%", width: "30px", height: "30px", cursor: "pointer", fontSize: "16px", fontWeight: "bold", display: "flex", alignItems: "center", justifyContent: "center" },
    validationError: { color: "red", fontSize: "12px", marginTop: "5px" },
    buttonGroup: { display: "flex", gap: "20px", marginTop: "30px" },
    formSection: { display: "flex", gap: "20px", marginBottom: "20px", flexWrap: "wrap" },
    formField: { flex: 1 },
    tableActions: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", flexWrap: "wrap" },
  };

  if (loading) return <div style={containerStyle}>Loading invoice data...</div>;
  if (error) return (
    <div style={containerStyle}>
      <div style={{color:"red"}}>{error}</div>
    </div>
  );

  return (
    <div style={containerStyle}>
      <form onSubmit={handleSubmit}>
        <div style={styles.patientInfo}>
          <h3>Invoice Details</h3>
          <div style={styles.formSection}>
            <div style={styles.formField}>
              <label style={styles.label}>Invoice Date:</label>
              <input
                type="date"
                value={formData.invoice_date}
                onChange={(e) => setFormData(prev => ({ ...prev, invoice_date: e.target.value }))}
                style={styles.selectInput}
                required
              />
              {validationErrors.invoice_date && <div style={styles.validationError}>{validationErrors.invoice_date[0]}</div>}
            </div>
          </div>
        </div>

        <div style={styles.patientInfo}>
          <div style={styles.tableActions}>
            <h3>Invoice Items</h3>
            <button type="button" onClick={addNewItem} style={styles.addButton}>+ Add Item</button>
          </div>

          {validationErrors.products && <div style={styles.validationError}>{validationErrors.products[0]}</div>}

          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.tableHeader}>S.No.</th>
                <th style={styles.tableHeader}>Product/Service</th>
                <th style={styles.tableHeader}>Quantity</th>
                <th style={styles.tableHeader}>Unit Price</th>
                <th style={styles.tableHeader}>Total</th>
                <th style={styles.tableHeader}>Action</th>
              </tr>
            </thead>
            <tbody>
              {formData.items.map((item, index) => {
                const itemTotal = (Number(item.amount) || 0) * (Number(item.quantity) || 0);
                return (
                  <tr key={index}>
                    <td style={styles.tableCell}>{index + 1}</td>
                    <td style={styles.tableCell}>
                      <select value={item.product_id || ""} onChange={(e) => handleItemChange(index, "product_id", e.target.value)} style={styles.selectInput}>
                        <option value="">-- Select Product --</option>
                        {products.map(product => (
                          <option key={product.product_id} value={product.product_id}>
                            {product.product_name} - ₹{product.final_price}
                          </option>
                        ))}
                      </select>
                      {!item.product_id && <input type="text" placeholder="Or enter description manually" value={item.description} onChange={(e) => handleItemChange(index, "description", e.target.value)} style={{...styles.selectInput, marginTop:"5px"}} />}
                    </td>
                    <td style={styles.tableCell}><input type="number" min="1" value={item.quantity} onChange={(e) => handleItemChange(index, "quantity", e.target.value)} style={styles.numberInput} /></td>
                    <td style={styles.tableCell}><input type="number" min="0" step="0.01" value={item.amount} onChange={(e) => handleItemChange(index, "amount", e.target.value)} style={styles.numberInput} disabled={item.product_id} /></td>
                    <td style={styles.tableCell}>₹{itemTotal.toFixed(2)}</td>
                    <td style={styles.tableCell}><button type="button" onClick={() => removeItem(index)} style={styles.removeButton}>×</button></td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{backgroundColor: "#f8f9fa"}}>
                <td colSpan="4" style={{...styles.tableCell, textAlign:"right", fontWeight:"bold"}}>Subtotal:</td>
                <td style={styles.tableCell}>₹{totalAmount.toFixed(2)}</td>
                <td></td>
              </tr>
              <tr style={{backgroundColor: "#f8f9fa"}}>
                <td colSpan="3"></td>
                <td style={{...styles.tableCell, textAlign:"right", fontWeight:"bold"}}>Discount (₹):</td>
                <td style={styles.tableCell}>
                  <input type="number" min="0" step="0.01" value={formData.discount} onChange={(e) => setFormData(prev => ({...prev, discount: Number(e.target.value || 0)}))} style={styles.discountInput} />
                </td>
                <td></td>
              </tr>
              <tr style={{backgroundColor: "#e9ecef", fontWeight:"bold"}}>
                <td colSpan="4" style={{...styles.tableCell, textAlign:"right"}}>Total After Discount:</td>
                <td style={styles.tableCell}>₹{totalAfterDiscount.toFixed(2)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div style={styles.buttonGroup}>
          <button
            type="submit"
            disabled={submitting || !patient}
            style={{
              ...styles.submitButton,
              ...(submitting || !patient ? styles.submitButtonDisabled : {}),
              marginBottom: "30px"
            }}
          >
            {submitting ? "Updating Invoice..." : "Update Invoice"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditPatientInvoice;